const constants = {
  COOKIES_VERSION: 'cookiesVersion',
  DEFAULT_LANGUAGE: 'en',
  VIDEO_DEVICE_ID: 'videoInputId',
  AUDIO_INPUT_DEVICE_ID: 'audioInputId',
  STORAGE_LANGUAGE_KEY: 'lng',
  VIDEO_STATE: 'isVideoActive',
  AUDIO_STATE: 'isAudioActive',
  SMS_CODE_LENGTH: 6,
  TOOLTIP_GROUP_DELAY: 500,
  QUEUES_FETCH_LIMIT: 15,
  CONSULTATIONS_LIST_LIMIT: 25,
  DEFAULT_NAME: 'Guest',
  DELETED_ROOM_IDS_KEY: 'deletedRooms',
  PARTICIPANT_DETAILS_ID: 'participant-details',
  ZOOM_CONTROLS_ID: 'zoom-controls',
  RECORDING_CONTROLS_ID: 'recording-controls',
  ENABLE_PUSH_NOTIFICATIONS_PROMPT_STATE: 'enableNotificationsPromptBlockedAt',
  REDIRECT_INITIATED: 'redirect-initiated',
  IS_LOCAL_STORAGE_LOGGED_IN: 'isLoggedIn',
  LIVEKIT_SCREEN_SHARING_LIMIT: 4,
  LIVEKIT_CLIENT_ID: 'livekitClientId',
  DEFAULT_COUNTRY_CODE: 'DK',
  MATRIX_URL: 'matrixUrl',
  MATRIX_HOMESERVER_BASE_URL: 'matrixHomeserverBaseUrl',
  MATRIX_LOGIN_URL: 'matrixLoginUrl',
  MAIN_WINDOW_ID: 'main-window-id',
  MAIN_WINDOW_PING: 'main-window-ping',
  SESSION_WINDOW_ID: 'session-window-id',
  SHOWN_CALENDAR_SYNC: 'shown-calendar-sync',
  WORKSPACE_INVITE_TOKEN: 'workspaceInviteToken',
  AUTH_PAGE_VISITED: 'auth_page_visited',
  SIDEBAR_OPEN: 'sidebar-open',
  WORKSPACE_ID: 'workspace_id',
  SEARCH_INDEX_DB_NAME: 'chat-s-index',
  SEARCH_INDEX_STORE_NAME: 'room-indexes',
  SEARCH_INDEX_PERSIST_DEBOUNCE_MS: 2_000,
  VIDEO_ROOMS_LIST_POLL_MS: 30_000,
  /** Follow-up listScheduledMeetings calls after Matrix invite — backend mx_users link can lag. */
  VIDEO_ROOMS_LIST_INVITE_RETRY_DELAYS_MS: [2_000, 5_000] as const,
  BANNER_PEEK_TRIGGER_PX: 25,
  BANNER_PEEK_DELAY_MS: 500,
  // Staleness expiry for instant messenger calls — written into the
  // MESSENGER_CALL state event so useActiveMeeting can auto-clear the
  // banner if the normal hangup cleanup path fails.
  MAX_INSTANT_CALL_MS: 8 * 60 * 60 * 1000,
  // Hard cap on simultaneously displayed active meetings per messenger room.
  // The pagination slider becomes unusable beyond this; a single room rarely
  // has more than 2-3 concurrent calls in practice. Anything past the cap
  // silently drops off (rarest entries first because they're appended last).
  MAX_ACTIVE_MEETINGS: 10,
  SKIP_LOBBY: 'skip_lobby',
};

export enum FeatureFlag {
  RAISE_HAND = 'CONNECT_FE_ENABLE_RAISE_HAND_PERM',
  MEETING_CHAT = 'CONNECT_FE_ENABLE_MEETING_CHAT_PERM',
  FEEDBACK_FORM = 'CONNECT_FE_ENABLE_FEEDBACK_FORM_PERM',
  ROOM_E2EE = 'FE_ENABLE_ROOM_E2EE_TEMP',
  BREAKOUT_ROOMS = 'CONNECT_FE_ENABLE_BREAKOUT_ROOMS',
  FE_ENABLE_CALENDAR_VIEW_PERM = 'FE_ENABLE_CALENDAR_VIEW_PERM',
  NEW_INVITATIONS_MODAL = 'FE_ENABLE_NEW_INVITATIONS_MODAL_TEMP',
  CALENDAR_SYNC = 'CONNECT_FE_ENABLE_CALENDAR_SYNC_PERM',
  WORKFLOWS = 'FE_ENABLE_WORKFLOWS_TEMP',
  DIRECT_MESSAGING = 'FE_ENABLE_DIRECT_MESSAGING_PERM',
  FE_ENABLE_MESSENGER = 'FE_ENABLE_MESSENGER_PERM',
  FE_ENABLE_CROSS_SIGNING = 'FE_ENABLE_CROSS_SIGNING_PERM',
  FE_ENABLE_WHITEBOARD = 'FE_ENABLE_WHITEBOARD',
  FE_ENFORCE_SECURITY_CODE_PERM = 'FE_ENFORCE_SECURITY_CODE_PERM',
  FE_ENABLE_CHAT_MESSAGE_SEARCH = 'FE_ENABLE_CHAT_MESSAGE_SEARCH_PERM',
  FE_ENABLE_MESSENGER_INSTANT_MEETINGS = 'FE_ENABLE_MESSENGER_INSTANT_MEETINGS_PERM',
  FE_ENABLE_MESSENGER_SCHEDULED_MEETINGS = 'FE_ENABLE_MESSENGER_SCHEDULED_MEETINGS_PERM',
}

// When Unleash is not configured (e.g. eu-lisa on-premise deployment), all
// flags default to enabled except those listed here. This matches prod-eu
// where whiteboard is the only flag not enabled.
export const featureFlagsDisabledByDefault: FeatureFlag[] = [FeatureFlag.FE_ENABLE_WHITEBOARD];

export const initialZoomState = {
  tileScale: 1,
  percentX: 0,
  percentY: 0,
};

export const COOKIE_KEY_PREFIX = 'connect';

export const matrixConstants = {
  MX_DEVICE_ID: `${COOKIE_KEY_PREFIX}_matrixDeviceId`,
  MX_USER_ID: `${COOKIE_KEY_PREFIX}_matrixUserId`,
  MX_ACCESS_TOKEN: `${COOKIE_KEY_PREFIX}_matrixAccessToken`,
  MX_REFRESH_TOKEN: `${COOKIE_KEY_PREFIX}_matrixRefreshToken`,
  MX_ACCESS_TOKEN_EXPIRES_AT: `${COOKIE_KEY_PREFIX}_matrixAccessTokenExpiresAt`,
  MX_SEARCH_SECRET: `${COOKIE_KEY_PREFIX}_matrixSearchSecret`,
  MX_DISPLAY_NAME: `${COOKIE_KEY_PREFIX}_matrixDisplayName`,
  MX_LOGGED_IN: `${COOKIE_KEY_PREFIX}_matrixLoggedIn`,
  MATRIX_STORES_VERSION: `${COOKIE_KEY_PREFIX}_matrixStoresVersion`,
  MATRIX_CLIENT_HEADER_NAME: 'X-Meedio-Client',
  MATRIX_CLIENT_HEADER_VALUE: 'true',
};

export const externalLinks = {
  DOMAIN: '.meedio.me',
  SYSTEM_PREFERENCES: 'x-apple.systempreferences:com.apple.preference.security?Privacy_ScreenCapture',
  CHROME: 'https://www.google.com/chrome',
  SAFARI: 'https://apps.apple.com/no/app/safari',
};

export default constants;
